import React, { useMemo } from 'react';
import PlayingCard from './PlayingCard';
import { SEAT_LABELS, visualSeatClass } from '../utils/tableSeats';
import { trickOffset } from '../utils/trickAnimation';

const MINI_W = 34;
const MINI_H = 48;
/** Scaled trickDist so mini cards sit in cardinal slots inside the panel box. */
const MINI_TRICK_DIST = 28;

/**
 * Optional last-trick history — same cardinal layout as the table, rotated to
 * the viewer's perspective (no N/E/S/W labels needed).
 */
function LastTrickPanel({
  lastTrick,
  open,
  onToggle,
  viewerSeat = null,
}) {
  const plays = lastTrick?.plays || [];
  const hasTrick = plays.length > 0;
  const winnerLabel = lastTrick?.winnerSeat
    ? (SEAT_LABELS[lastTrick.winnerSeat] || lastTrick.winnerSeat)
    : '—';

  const layoutPlays = useMemo(() => {
    if (!plays.length) return [];
    return plays.map((play, idx) => {
      const visual = visualSeatClass(play.seat, viewerSeat);
      const off = trickOffset(visual, MINI_TRICK_DIST);
      return {
        key: `${play.seat}-${play.card?.id || idx}`,
        card: play.card,
        visual,
        style: {
          transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${off.rot}deg)`,
          zIndex: idx + 1,
        },
      };
    });
  }, [plays, viewerSeat]);

  // Box large enough for ±trickDist + half card
  const boxW = MINI_TRICK_DIST * 2 + MINI_W + 12;
  const boxH = MINI_TRICK_DIST * 2 + MINI_H + 12;

  return (
    <div className="spades-last-trick-dock">
      <button
        type="button"
        className={[
          'spades-last-trick-toggle',
          open ? 'spades-last-trick-toggle--open' : '',
        ].filter(Boolean).join(' ')}
        onClick={onToggle}
        disabled={!hasTrick && !open}
        title={hasTrick ? 'Show or hide the previous trick' : 'No previous trick yet'}
      >
        {open ? 'Hide last trick' : 'Last trick'}
      </button>
      {open && hasTrick && (
        <div className="spades-last-trick-panel" role="region" aria-label="Last trick">
          <div className="spades-last-trick-head">
            <span>
              Hand {lastTrick.handNumber ?? '—'}
            </span>
            <span className="spades-last-trick-winner">
              Won by {winnerLabel}
            </span>
          </div>
          <div
            className="spades-last-trick-stage"
            style={{ width: boxW, height: boxH }}
            aria-hidden="false"
          >
            {layoutPlays.map((p) => (
              <div
                key={p.key}
                className={`spades-last-trick-card spades-last-trick-card--${p.visual}`}
                style={p.style}
              >
                {p.card ? (
                  <PlayingCard card={p.card} width={MINI_W} height={MINI_H} />
                ) : (
                  <PlayingCard faceDown width={MINI_W} height={MINI_H} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default LastTrickPanel;
